import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { useContent } from "../lib/content";
import { Database, ArrowLeft, ArrowRight } from "lucide-react";

const COLOR = {
  blue:   { border: "#3B82F6", text: "text-[#3B82F6]", bg: "bg-[#3B82F6]" },
  purple: { border: "#A855F7", text: "text-[#A855F7]", bg: "bg-[#A855F7]" },
  green:  { border: "#00FF94", text: "text-[#00FF94]", bg: "bg-[#00FF94]" },
};

export default function DataProtection() {
  const { tField } = useI18n();
  const { content } = useContent();
  const dp = content?.data_protection;

  return (
    <div className="mx-auto max-w-5xl px-6 lg:px-10 py-12 lg:py-20">
      <Link
        to="/"
        data-testid="dataprotection-back-link"
        className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] hover:text-white mb-10"
      >
        <ArrowLeft className="h-3 w-3" strokeWidth={2} />
        Back
      </Link>

      <div className="flex items-start gap-4 mb-3">
        <div className="h-10 w-10 border border-[#3B82F6] text-[#3B82F6] flex items-center justify-center shrink-0">
          <Database className="h-5 w-5" strokeWidth={1.5} />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-2">
            // DATA PROTECTION · INTERNATIONAL
          </div>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tight text-white" style={{ fontWeight: 800 }}>
            {tField(dp?.title) || "Data Protection"}
          </h1>
        </div>
      </div>

      <p className="text-sm text-[#94A3B8] leading-relaxed max-w-3xl mb-12">
        {tField(dp?.subtitle)}
      </p>

      {/* Section label "対象法規" */}
      <div className="flex items-center gap-3 mb-6">
        <div className="h-px flex-1 bg-[#1E2A3F]" />
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#3B82F6]">
          【{tField(dp?.section_label) || "Target regulations"}】
        </div>
        <div className="h-px flex-1 bg-[#1E2A3F]" />
      </div>

      {/* Region groups */}
      <div className="space-y-4">
        {(dp?.regions || []).map((r, i) => {
          const col = COLOR[r.color] || COLOR.blue;
          return (
            <article
              key={i}
              data-testid={`dataprotection-region-${i}`}
              className="bg-[#0F1525] border border-[#1E2A3F] hover:border-[var(--ac)] transition-colors"
              style={{ "--ac": col.border }}
            >
              {/* Region header */}
              <div className="flex items-center gap-3 px-6 py-3 border-b border-[#1E2A3F]">
                <div className={`h-6 w-1.5 ${col.bg}`} />
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#64748B]">
                  ■
                </div>
                <h2 className="font-display text-xl tracking-tight text-white" style={{ fontWeight: 800 }}>
                  {tField(r.region)}
                </h2>
              </div>

              {/* Laws */}
              <div className="px-6 py-5 space-y-3">
                {(r.laws || []).map((law, li) => (
                  <div key={li} className="flex items-baseline gap-3">
                    <span className={`font-mono text-[10px] ${col.text} shrink-0 mt-0.5`}>·</span>
                    <div className="flex-1">
                      <div className="font-display text-base text-white" style={{ fontWeight: 700 }}>
                        {tField(law.name)}
                      </div>
                      {law.fullname && (
                        <div className="text-xs text-[#94A3B8] mt-0.5 leading-relaxed">
                          （{tField(law.fullname)}）
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </article>
          );
        })}
      </div>

      {dp?.footnote && (
        <div className="mt-12 border-t border-[#1E2A3F] pt-6 flex items-center justify-between gap-4 flex-wrap">
          <p className="text-sm text-[#94A3B8] leading-relaxed">{tField(dp.footnote)}</p>
          <Link
            to="/compliance"
            data-testid="dataprotection-to-compliance"
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#3B82F6]/40 text-[#3B82F6] hover:bg-[#3B82F6]/10 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors"
          >
            View compliance details
            <ArrowRight className="h-3 w-3" strokeWidth={2} />
          </Link>
        </div>
      )}
    </div>
  );
}
